/**
 * Find a tool by name and go there - the command palette, React edition of
 * ui/ToolFinder.ts, hosted by a bare antd Modal (the .tf-box card brings its
 * own chrome).
 *
 * Searches everything a tool is called - asset tag, hardware name, unit
 * process - because students know tools by different handles. The list shows
 * every tool in AISLE ORDER when the query is empty, which quietly doubles
 * as a map of the process.
 */

import { Modal } from 'antd'
import { useEffect, useMemo, useRef, useState } from 'react'
import { TOOLS, toolName, toolProcess, type Tool } from '../../fab/layout'
import { PLACES, placeName, placeWhere, type Place } from '../../fab/places'
import { t } from '../../i18n/i18n'
import { useLang } from '../i18n/LanguageProvider'
import { LocaleHtml } from '../i18n/LocaleHtml'

/**
 * A row is a tool or a place. Places come after the last station, so the
 * empty-query list still reads as the aisle - with the stair at the end,
 * which is where it is.
 */
type Entry = Tool | Place
const isTool = (e: Entry): e is Tool => 'assetTag' in e
const entryTag = (e: Entry): string => (isTool(e) ? e.assetTag : e.tag)
const entryName = (e: Entry): string => (isTool(e) ? toolName(e) : placeName(e))
const entryWhere = (e: Entry): string => (isTool(e) ? toolProcess(e) : placeWhere(e))
const ENTRIES: Entry[] = [...TOOLS, ...PLACES]

/**
 * Where the finder picks up when it reopens, remembered per machine.
 *
 * The entry last jumped to comes back SELECTED and centred, so a student
 * working one tool over and over opens the finder and presses Enter - no
 * scrolling down a forty-row aisle to where they were. The raw list offset
 * is the fallback for a finder that was browsed and closed without a jump.
 * Both are browse state only: under a typed query the list is filtered and
 * neither means anything.
 */
const LAST_KEY = 'simfab.finder.last'
const SCROLL_KEY = 'simfab.finder.scroll'

const readStored = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const writeStored = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Storage blocked - the finder just opens at the top next time.
  }
}

/** The row to open on: the last jump's entry if it still exists, else the top. */
const initialActive = (): number => {
  const last = readStored(LAST_KEY)
  const idx = last ? ENTRIES.findIndex((e) => e.id === last) : -1
  return idx >= 0 ? idx : 0
}

export function ToolFinderDialog({
  onJump,
  onClose,
}: {
  onJump: (target: Entry) => void
  onClose: () => void
}) {
  useLang()
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(initialActive)
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Focus the input AFTER antd's own focus management has run - the Modal
  // focuses its wrapper on open, which beats a plain autoFocus attribute and
  // left every keystroke falling through to the floor.
  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 50)
    return () => window.clearTimeout(id)
  }, [])

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase()
    return q
      ? ENTRIES.filter((entry) =>
          [entryTag(entry), entry.id, entryName(entry), entryWhere(entry)].some((name) =>
            name.toLowerCase().includes(q),
          ),
        )
      : [...ENTRIES]
  }, [query])
  const cursor = Math.min(active, Math.max(matches.length - 1, 0))

  const close = (): void => {
    // Keep the browse position for next time - but never clobber it with a
    // filtered list's offset.
    if (!query.trim() && listRef.current) {
      writeStored(SCROLL_KEY, String(Math.round(listRef.current.scrollTop)))
    }
    onClose()
  }

  const jump = (tool: Entry): void => {
    writeStored(LAST_KEY, tool.id)
    close()
    onJump(tool)
  }

  // On open: the last-jumped row, already selected, is centred; failing
  // that the list goes back to where it was scrolled. After that the active
  // row is kept in view as the arrows move it. ONE effect, in that order -
  // as two, the keep-in-view pass for row 0 ran after the restore and
  // scrolled the list straight back to the top, which is why the remembered
  // offset never seemed to take.
  const opened = useRef(false)
  useEffect(() => {
    const list = listRef.current
    if (!list) return
    if (!opened.current) {
      opened.current = true
      if (cursor > 0) {
        list.querySelector('.tf-row.active')?.scrollIntoView({ block: 'center' })
      } else {
        const saved = Number(readStored(SCROLL_KEY))
        if (saved > 0) list.scrollTop = saved
      }
      return
    }
    list.querySelector('.tf-row.active')?.scrollIntoView({ block: 'nearest' })
  }, [cursor, matches])

  return (
    <Modal
      open
      onCancel={close}
      footer={null}
      title={null}
      closable={false}
      className="bare-modal"
      width="min(560px, 92vw)"
      style={{ top: '12vh' }}
    >
      <div className="tf-box">
        <input
          ref={inputRef}
          className="tf-input"
          type="text"
          placeholder={t('finder.Placeholder')}
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setActive(0)
          }}
          onKeyDown={(e) => {
            // The fab underneath listens for WASD and E; nothing typed here
            // may reach it.
            e.stopPropagation()
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setActive(Math.min(cursor + 1, matches.length - 1))
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setActive(Math.max(cursor - 1, 0))
            } else if (e.key === 'Enter') {
              e.preventDefault()
              const hit = matches[cursor]
              if (hit) jump(hit)
            } else if (e.key === 'Escape') {
              e.preventDefault()
              close()
            }
          }}
        />
        <div className="tf-list" ref={listRef}>
          {matches.length === 0 ? (
            <div className="tf-empty">{t('finder.Empty', { q: query.trim() })}</div>
          ) : (
            matches.map((tool, i) => (
              <div
                key={tool.id}
                className={i === cursor ? 'tf-row active' : 'tf-row'}
                onClick={() => jump(tool)}
                // Mouse movement re-aims the keyboard cursor, so Enter always
                // takes the row under the pointer rather than a stale one.
                onMouseMove={() => {
                  if (cursor !== i) setActive(i)
                }}
              >
                <span className="tf-tag">{entryTag(tool)}</span>
                <span className="tf-body">
                  <span className="tf-proc">{entryWhere(tool)}</span>
                  <span className="tf-name">{entryName(tool)}</span>
                </span>
              </div>
            ))
          )}
        </div>
        <LocaleHtml as="div" className="tf-hint" k="finder.Hint" />
      </div>
    </Modal>
  )
}
